import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';

import { ServicePlan } from '@/data/models/service-plan.model';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useAppSelector } from '@/hooks/useAppSelector';
import * as lookupSlice from '@/data/lookup.slice';

import OverrideForm from '../../home/reservations/OverrideForm';
import type {
  ReservationFormInput,
  ReservationFormValues,
} from '../schema/reservationForm.schema';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';

const PREFIX = 'RequestOverrideDialog';

const classes = {
  content: `${PREFIX}-content`,
  message: `${PREFIX}-message`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.message}`]: {
    marginBottom: theme.spacing(2),
  },
}));

type OverrideDialogFormValues = {
  OverrideFirstName: string;
  OverrideLastName: string;
  OverrideReason: string;
  ServicePlan: string;
  AllowOverride: boolean;
};

interface RequestOverrideDialogProps {
  servicePlans: ServicePlan[];
  reservationForm: UseFormReturn<
    ReservationFormInput,
    unknown,
    ReservationFormValues
  >;
}

export default function RequestOverrideDialog({
  servicePlans,
  reservationForm,
}: RequestOverrideDialogProps) {
  const t = useCommonTranslation();
  const { mainActor, mainSnapshot } = useReservationFormMachines();

  const overrideReasons = useAppSelector(
    lookupSlice.selectOverrideAuthorizationReasons,
  );

  const requestOverrideRef = mainSnapshot.context.requestOverrideRef;

  const [dialogOpen, setDialogOpen] = useState(() =>
    Boolean(requestOverrideRef?.getSnapshot().matches('open')),
  );

  useEffect(() => {
    if (!requestOverrideRef) return;
    setDialogOpen(requestOverrideRef.getSnapshot().matches('open'));
    const sub = requestOverrideRef.subscribe((snap) => {
      setDialogOpen(snap.matches('open'));
    });
    return () => sub.unsubscribe();
  }, [requestOverrideRef]);

  const formHook = useForm({
    defaultValues: {
      OverrideFirstName: '',
      OverrideLastName: '',
      OverrideReason: '',
      ServicePlan: '',
      AllowOverride: false,
    },
  });

  const isOverrideChecked = formHook.watch('AllowOverride');

  const closeRequestOverride = () => {
    requestOverrideRef?.send({ type: 'CLOSE' });
  };

  const handleSave = (data: OverrideDialogFormValues) => {
    reservationForm.setValue('AllowOverride', data.AllowOverride);
    reservationForm.setValue('OverrideFirstName', data.OverrideFirstName);
    reservationForm.setValue('OverrideLastName', data.OverrideLastName);
    reservationForm.setValue('OverrideReason', data.OverrideReason);
    reservationForm.setValue('OverrideServicePlan', data.ServicePlan);
    formHook.reset();
    closeRequestOverride();
    mainActor.send({ type: 'OVERRIDE_OPEN_SUCCESS' });
    void reservationForm.trigger();
  };

  const handleClose = () => {
    formHook.reset();
    closeRequestOverride();
  };

  return (
    <RootDialog open={dialogOpen} onClose={handleClose}>
      <DialogTitle>{t('reservation.requestOverride')}</DialogTitle>
      <DialogContent className={classes.content}>
        <Typography className={classes.message}>
          {t('reservation.overrideMessage')}
        </Typography>
        <OverrideForm
          overrideReasons={overrideReasons}
          servicePlans={servicePlans}
          formHook={formHook}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.close')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!formHook.formState.isValid || !isOverrideChecked}
          onClick={formHook.handleSubmit(handleSave)}
          data-fs-element="CreateReservationRequestOverrideSubmitButton">
          {t('common.done')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
